// Libraries
import { Box } from '@chakra-ui/react';
import { useState } from 'react';
import { toFormikValidationSchema } from 'zod-formik-adapter';
// Components
import { FormError, FormSubmit } from '@atoms/index';
import { useForm, useSendResetPassword } from '@hooks/index';
import { FormInput } from '@molecules/index';
import { ForgotPasswordFormProps } from './ForgotPasswordForm.type';
// Data
import { ForgotPasswordSchema, ForgotPasswordValues } from '@schemas/forms';
import { ForgotPasswordStatus } from '@stores/index';
// Utils
import { getErrorMessage } from '@helpers/index';
import text from '@translations/en.json';

/**
 * The initial values for the forgot password form.
 */
const initialValues: ForgotPasswordValues = {
  username: '',
};

/**
 * ForgotPasswordForm component represents a form for user forgot password action.
 * @returns {ReactElement} The ForgotPasswordForm component.
 */
export const ForgotPasswordForm = ({ setStatus, setUsername }: ForgotPasswordFormProps) => {
  const { sendResetPasswordEmail, getTimeToNextMail } = useSendResetPassword();
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const buttonMt = !!errorMessage ? 3 : 13;

  /**
   * Handles form submission.
   * @param {LoginValues} values - The form values to be submitted.
   */
  const submitHandler = async (values: ForgotPasswordValues) => {
    try {
      const { username } = values;
      const timeToNextMail = getTimeToNextMail(`${username}`);
      setIsLoading(true);
      setUsername(username);

      if (timeToNextMail) {
        // The wait for the next try is not completed and the user is retrying after a reload
        setStatus(ForgotPasswordStatus.FORCED_RETRY);
      } else {
        // The user is able to send the next mail
        await sendResetPasswordEmail(username);
        setStatus(ForgotPasswordStatus.EMAIL_SENT);
      }

      setIsLoading(false);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setErrorMessage(errorMessage);
      setIsLoading(false);
    }
  };

  const formikCtx = useForm<ForgotPasswordValues>({
    initialValues,
    schema: toFormikValidationSchema(ForgotPasswordSchema),
    submitHandler,
  });
  const { submitCount } = formikCtx;

  return (
    <Box
      as='form'
      display='flex'
      flexDirection='column'
      alignItems='center'
      gap={3.5}
      onSubmit={formikCtx.handleSubmit}
      w={72}
      data-testid='forgot-password-form'
    >
      <FormInput
        isRequired
        label={text.username_field_label}
        wasSubmitted={submitCount > 0}
        id='username'
        autoComplete='username'
        value={formikCtx.values.username}
        onChange={formikCtx.handleChange}
        error={formikCtx.errors.username}
      />
      {!!errorMessage && (
        <Box w='full' h={9}>
          <FormError>{errorMessage}</FormError>
        </Box>
      )}
      <FormSubmit mt={buttonMt} isLoading={isLoading} loadingText={text.sending_email}>
        {text.reset_password}
      </FormSubmit>
    </Box>
  );
};
